import React, { memo, useCallback, useLayoutEffect, useState } from 'react'
import { Icon, Text } from 'common-components'
import InfoIcon from 'assets/svgr-icons/Info.svg'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { getIsTouchDevice } from 'utils'
import { getRatingColor } from '../ratingColor'
import * as Styled from './styled'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'

interface IInfoTooltipProps {
  description: string
  rating: number | null
}

const VIEWPORT_MARGIN = 8
const IS_TOUCH_DEVICE = getIsTouchDevice()

const InfoTooltip: React.FC<IInfoTooltipProps> = ({ description, rating }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [arrowSide, setArrowSide] = useState<'top' | 'bottom'>('bottom')
  const [viewportShiftPx, setViewportShiftPx] = useState(0)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(prev => !prev), [])

  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  useLayoutEffect(() => {
    if (!isOpen || !anchorEl || !panelEl) return
    const anchorRect = anchorEl.getBoundingClientRect()
    const panelRect = panelEl.getBoundingClientRect()
    setArrowSide(panelRect.top < anchorRect.top ? 'bottom' : 'top')

    // Clamp the panel inside the viewport: if its left/right edge spills out,
    // push the panel back in and remember the offset so the arrow can shift
    // the opposite direction to stay anchored to the icon. The panel rect
    // already includes the previous shift, so subtract it before re-measuring
    // to avoid compounding across re-opens.
    setViewportShiftPx(prevShift => {
      const unshiftedLeft = panelRect.left - prevShift
      const unshiftedRight = panelRect.right - prevShift
      if (unshiftedLeft < VIEWPORT_MARGIN) {
        return VIEWPORT_MARGIN - unshiftedLeft
      }
      if (unshiftedRight > window.innerWidth - VIEWPORT_MARGIN) {
        return window.innerWidth - VIEWPORT_MARGIN - unshiftedRight
      }
      return 0
    })
  }, [isOpen, anchorEl, panelEl])

  const trimmed = description?.trim()
  if (!trimmed && rating === null) return null

  return (
    <>
      <Styled.InfoIconAnchor
        ref={setAnchorEl}
        onMouseEnter={IS_TOUCH_DEVICE ? undefined : open}
        onMouseLeave={IS_TOUCH_DEVICE ? undefined : close}
        onClick={(e: React.MouseEvent) => {
          if (isMobile) {
            e.stopPropagation()
            toggle()
          }
        }}
      >
        <Icon icon={<InfoIcon />} customSize={20} color="secondarySemiLight" />
      </Styled.InfoIconAnchor>
      <Styled.InfoTooltipFlyout
        isOpen={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        exit={{ opacity: 0, transition: { duration: 0 } }}
        options={{
          position: 'top',
          alignmentX: 'left',
          extraOffset: 10
        }}
      >
        <Styled.TooltipPanel
          ref={setPanelEl}
          $viewportShiftPx={viewportShiftPx}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
          }}
        >
          {trimmed && (
            <>
              <Text type="h5" color="primarySemiLight" fontWeight="700">
                {translate(T.VIDEO_MODEL_PICK_FOR_LABEL, { isUpper: true })}
              </Text>
              <Styled.DescriptionWrapper>
                <Text
                  type="h4"
                  color="white"
                  fontWeight="400"
                  lineHeight="18px"
                >
                  {trimmed}
                </Text>
              </Styled.DescriptionWrapper>
            </>
          )}
          {trimmed && rating !== null && <Styled.TooltipDivider />}
          {rating !== null && (
            <Styled.QualityRow>
              <Text type="h5" color="primarySemiLight" fontWeight="700">
                {translate(T.VIDEO_MODEL_QUALITY_LABEL, { isUpper: true })}
              </Text>
              <Styled.QualityBarTrack>
                <Styled.QualityBarFill $score={rating} />
              </Styled.QualityBarTrack>
              <Text type="h3" customColor={getRatingColor(rating)}>
                {rating.toFixed(1)}/10
              </Text>
            </Styled.QualityRow>
          )}
          <Styled.TooltipArrow
            $side={arrowSide}
            $viewportShiftPx={viewportShiftPx}
          />
        </Styled.TooltipPanel>
      </Styled.InfoTooltipFlyout>
    </>
  )
}

export default memo(InfoTooltip)
